import type { LangMap } from "./index";

export const planner: LangMap = {
  en: {
    title: "Planner",
    subtitle: "Your smart study calendar.",
    today: "Today",
    addEvent: "Add event",
    noEventsToday: "No events scheduled for this day.",
    allDayShort: "Day",
    circleColor: "Circle color:",
    circlePreview: "Circle preview:",
    eventTypes: {
      verifica: "Test",
      compito: "Homework",
      orale: "Oral exam",
      hobby: "Hobby",
      event: "Event",
      task: "Task",
    },
    autoPlanning: {
      title: "Automatic planning",
      description: "AI will schedule tests, reviews and breaks when you add an exam or sync your gradebook.",
    },
    edit: {
      title: "Edit event",
      titleReadOnly: "Event details",
      description: "Edit title, date, time, type or notes.",
      descriptionImported: "Event imported from {{source}}: only partially editable.",
    },
    add: {
      title: "New event",
      description: "The circle color on the calendar follows the event type.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Homework from Google Classroom",
      description: "Paste the secret iCal link of the course calendar: I import deadlines and homework without asking for your password.",
      previewDesc: "Check what I found and confirm.",
      urlLabel: "Calendar iCal link",
      help1: "Open Google Classroom → course → Classwork → Google Calendar.",
      help2: "In Google Calendar open the course calendar settings.",
      help3: "Copy the secret address in iCal format and paste it here.",
      countItems: "Homework / deadlines",
      countSubjects: "Subjects found",
      empty: "No upcoming deadlines found in this calendar.",
      optCalendar: "Add to calendar",
      optHomework: "Add to homework list",
      load: "Read the calendar",
      confirm: "Import",
      imported: "Imported {{events}} events and {{homework}} homework from Classroom",
    },
    classeviva: {
      trigger: "Gradebook",
      title: "Sync ClasseViva",
      description: "Import homework, tests and agenda events. Your credentials are not saved: they are used only for this sync.",
      uidLabel: "User code / email",
      uidPlaceholder: "E.g. S1234567L",
      passLabel: "Password",
      disclaimer: "Unofficial integration — if ClasseViva changes its API, importing may temporarily stop working.",
      syncing: "Syncing…",
      syncNow: "Sync now",
    },
    axios: {
      trigger: "Axios",
      title: "Sync Axios gradebook",
      description: "Import homework, tests and lesson topics from the Axios electronic register. Credentials are used only for this sync.",
      schoolLabel: "School code (tax code)",
      schoolPlaceholder: "E.g. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Wrong credentials: check username, password and the school code.",
      REGISTRO_TIMEOUT: "The gradebook did not answer in time. Try again in a minute.",
      REGISTRO_UNREACHABLE: "The gradebook is unreachable right now. Try again later.",
      REGISTRO_BAD_RESPONSE: "Unexpected answer from the gradebook: your school may not expose this API.",
      REGISTRO_MISSING_SCHOOL: "Enter your school code to connect Axios.",
    },
    fields: {
      title: "Title",
      type: "Type",
      subject: "Subject",
      subjectOptional: "Subject (optional)",
      date: "Date",
      time: "Time",
      allDay: "All day",
      notes: "Notes",
      notesOptional: "Notes (optional)",
    },
    placeholders: {
      title: "E.g. Math test",
      subject: "E.g. History",
    },
    actions: {
      delete: "Delete",
      close: "Close",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      add: "Add",
    },
    toast: {
      updated: "Event updated",
      deleted: "Event deleted",
      added: "Event added",
      syncSuccess: "Synced {{count}} events from ClasseViva",
    },
    errors: {
      mustBeLoggedIn: "You must be logged in",
    },
  },
  it: {
    title: "Planner",
    subtitle: "Il tuo calendario di studio intelligente.",
    today: "Oggi",
    addEvent: "Aggiungi evento",
    noEventsToday: "Nessun evento in programma per questo giorno.",
    allDayShort: "Giorno",
    circleColor: "Colore cerchio:",
    circlePreview: "Anteprima cerchio:",
    eventTypes: {
      verifica: "Verifica",
      compito: "Compito",
      orale: "Interrogazione orale",
      hobby: "Hobby",
      event: "Evento",
      task: "Attività",
    },
    autoPlanning: {
      title: "Pianificazione automatica",
      description: "L'IA distribuirà verifiche, ripassi e pause quando aggiungerai un esame o sincronizzerai il registro.",
    },
    edit: {
      title: "Modifica evento",
      titleReadOnly: "Dettagli evento",
      description: "Modifica titolo, data, orario, tipo o note.",
      descriptionImported: "Evento importato da {{source}}: modificabile solo in parte.",
    },
    add: {
      title: "Nuovo evento",
      description: "Il colore del cerchio nel calendario segue il tipo di evento.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Compiti da Google Classroom",
      description: "Incolla il link iCal segreto del calendario del corso: importo scadenze e compiti senza chiedere la password.",
      previewDesc: "Controlla cosa ho trovato e conferma.",
      urlLabel: "Link iCal del calendario",
      help1: "Apri Google Classroom → corso → Lavori del corso → Google Calendar.",
      help2: "In Google Calendar apri le impostazioni del calendario del corso.",
      help3: "Copia l'indirizzo segreto in formato iCal e incollalo qui.",
      countItems: "Compiti / scadenze",
      countSubjects: "Materie rilevate",
      empty: "Nessuna scadenza futura trovata in questo calendario.",
      optCalendar: "Aggiungi al calendario",
      optHomework: "Aggiungi alla lista compiti",
      load: "Leggi il calendario",
      confirm: "Importa",
      imported: "Importati {{events}} impegni e {{homework}} compiti da Classroom",
    },
    classeviva: {
      trigger: "Registro",
      title: "Sincronizza ClasseViva",
      description: "Importa compiti, verifiche ed eventi dell'agenda. Le credenziali non vengono salvate: vengono usate solo per questa sincronizzazione.",
      uidLabel: "Codice utente / email",
      uidPlaceholder: "Es. S1234567L",
      passLabel: "Password",
      disclaimer: "Integrazione non ufficiale — se ClasseViva cambia le sue API l'import potrebbe non funzionare temporaneamente.",
      syncing: "Sincronizzo…",
      syncNow: "Sincronizza ora",
    },
    axios: {
      trigger: "Axios",
      title: "Sincronizza registro Axios",
      description: "Importo compiti, verifiche e argomenti delle lezioni dal registro elettronico Axios. Le credenziali sono usate solo per questa sincronizzazione.",
      schoolLabel: "Codice scuola (codice fiscale)",
      schoolPlaceholder: "Es. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Credenziali errate: controlla utente, password e codice scuola.",
      REGISTRO_TIMEOUT: "Il registro non ha risposto in tempo. Riprova tra un minuto.",
      REGISTRO_UNREACHABLE: "Il registro non è raggiungibile in questo momento. Riprova più tardi.",
      REGISTRO_BAD_RESPONSE: "Risposta inattesa dal registro: la tua scuola potrebbe non esporre questa API.",
      REGISTRO_MISSING_SCHOOL: "Inserisci il codice della scuola per collegare Axios.",
    },
    fields: {
      title: "Titolo",
      type: "Tipo",
      subject: "Materia",
      subjectOptional: "Materia (opzionale)",
      date: "Data",
      time: "Ora",
      allDay: "Tutto il giorno",
      notes: "Note",
      notesOptional: "Note (opzionale)",
    },
    placeholders: {
      title: "Es. Verifica di Matematica",
      subject: "Es. Storia",
    },
    actions: {
      delete: "Elimina",
      close: "Chiudi",
      save: "Salva",
      saving: "Salvo…",
      cancel: "Annulla",
      add: "Aggiungi",
    },
    toast: {
      updated: "Evento aggiornato",
      deleted: "Evento eliminato",
      added: "Evento aggiunto",
      syncSuccess: "Sincronizzati {{count}} eventi da ClasseViva",
    },
    errors: {
      mustBeLoggedIn: "Devi essere loggato",
    },
  },
  es: {
    title: "Planificador",
    subtitle: "Tu calendario de estudio inteligente.",
    today: "Hoy",
    addEvent: "Añadir evento",
    noEventsToday: "No hay eventos programados para este día.",
    allDayShort: "Día",
    circleColor: "Color del círculo:",
    circlePreview: "Vista previa del círculo:",
    eventTypes: {
      verifica: "Examen",
      compito: "Tarea",
      orale: "Examen oral",
      hobby: "Hobby",
      event: "Evento",
      task: "Actividad",
    },
    autoPlanning: {
      title: "Planificación automática",
      description: "La IA distribuirá exámenes, repasos y descansos cuando añadas un examen o sincronices el registro.",
    },
    edit: {
      title: "Editar evento",
      titleReadOnly: "Detalles del evento",
      description: "Edita título, fecha, hora, tipo o notas.",
      descriptionImported: "Evento importado de {{source}}: editable solo en parte.",
    },
    add: {
      title: "Nuevo evento",
      description: "El color del círculo en el calendario sigue el tipo de evento.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Tareas de Google Classroom",
      description: "Pega el enlace iCal secreto del calendario del curso: importo fechas y tareas sin pedir tu contraseña.",
      previewDesc: "Revisa lo que encontré y confirma.",
      urlLabel: "Enlace iCal del calendario",
      help1: "Abre Google Classroom → curso → Trabajo de clase → Google Calendar.",
      help2: "En Google Calendar abre la configuración del calendario del curso.",
      help3: "Copia la dirección secreta en formato iCal y pégala aquí.",
      countItems: "Tareas / fechas",
      countSubjects: "Materias detectadas",
      empty: "No hay fechas próximas en este calendario.",
      optCalendar: "Añadir al calendario",
      optHomework: "Añadir a la lista de tareas",
      load: "Leer el calendario",
      confirm: "Importar",
      imported: "Importados {{events}} eventos y {{homework}} tareas de Classroom",
    },
    classeviva: {
      trigger: "Registro",
      title: "Sincronizar ClasseViva",
      description: "Importa tareas, exámenes y eventos de la agenda. Tus credenciales no se guardan: se usan solo para esta sincronización.",
      uidLabel: "Código de usuario / correo",
      uidPlaceholder: "Ej. S1234567L",
      passLabel: "Contraseña",
      disclaimer: "Integración no oficial — si ClasseViva cambia su API, la importación podría dejar de funcionar temporalmente.",
      syncing: "Sincronizando…",
      syncNow: "Sincronizar ahora",
    },
    axios: {
      trigger: "Axios",
      title: "Sincronizar registro Axios",
      description: "Importo tareas, exámenes y temas de las clases del registro electrónico Axios. Las credenciales solo se usan para esta sincronización.",
      schoolLabel: "Código del centro",
      schoolPlaceholder: "Ej. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Credenciales incorrectas: revisa usuario, contraseña y código del centro.",
      REGISTRO_TIMEOUT: "El registro no respondió a tiempo. Inténtalo en un minuto.",
      REGISTRO_UNREACHABLE: "El registro no está disponible ahora. Inténtalo más tarde.",
      REGISTRO_BAD_RESPONSE: "Respuesta inesperada del registro: puede que tu centro no ofrezca esta API.",
      REGISTRO_MISSING_SCHOOL: "Introduce el código del centro para conectar Axios.",
    },
    fields: {
      title: "Título",
      type: "Tipo",
      subject: "Materia",
      subjectOptional: "Materia (opcional)",
      date: "Fecha",
      time: "Hora",
      allDay: "Todo el día",
      notes: "Notas",
      notesOptional: "Notas (opcional)",
    },
    placeholders: {
      title: "Ej. Examen de Matemáticas",
      subject: "Ej. Historia",
    },
    actions: {
      delete: "Eliminar",
      close: "Cerrar",
      save: "Guardar",
      saving: "Guardando…",
      cancel: "Cancelar",
      add: "Añadir",
    },
    toast: {
      updated: "Evento actualizado",
      deleted: "Evento eliminado",
      added: "Evento añadido",
      syncSuccess: "Sincronizados {{count}} eventos de ClasseViva",
    },
    errors: {
      mustBeLoggedIn: "Debes iniciar sesión",
    },
  },
  fr: {
    title: "Planificateur",
    subtitle: "Votre calendrier d'étude intelligent.",
    today: "Aujourd'hui",
    addEvent: "Ajouter un événement",
    noEventsToday: "Aucun événement prévu pour ce jour.",
    allDayShort: "Jour",
    circleColor: "Couleur du cercle :",
    circlePreview: "Aperçu du cercle :",
    eventTypes: {
      verifica: "Contrôle",
      compito: "Devoir",
      orale: "Interrogation orale",
      hobby: "Loisir",
      event: "Événement",
      task: "Activité",
    },
    autoPlanning: {
      title: "Planification automatique",
      description: "L'IA répartira les contrôles, révisions et pauses lorsque vous ajouterez un examen ou synchroniserez le carnet de notes.",
    },
    edit: {
      title: "Modifier l'événement",
      titleReadOnly: "Détails de l'événement",
      description: "Modifiez le titre, la date, l'heure, le type ou les notes.",
      descriptionImported: "Événement importé de {{source}} : modifiable seulement en partie.",
    },
    add: {
      title: "Nouvel événement",
      description: "La couleur du cercle dans le calendrier suit le type d'événement.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Devoirs depuis Google Classroom",
      description: "Colle le lien iCal secret du calendrier du cours : j'importe les échéances et les devoirs sans mot de passe.",
      previewDesc: "Vérifie ce que j'ai trouvé et confirme.",
      urlLabel: "Lien iCal du calendrier",
      help1: "Ouvre Google Classroom → cours → Travaux et devoirs → Google Agenda.",
      help2: "Dans Google Agenda, ouvre les paramètres du calendrier du cours.",
      help3: "Copie l'adresse secrète au format iCal et colle-la ici.",
      countItems: "Devoirs / échéances",
      countSubjects: "Matières détectées",
      empty: "Aucune échéance à venir dans ce calendrier.",
      optCalendar: "Ajouter au calendrier",
      optHomework: "Ajouter à la liste des devoirs",
      load: "Lire le calendrier",
      confirm: "Importer",
      imported: "{{events}} événements et {{homework}} devoirs importés depuis Classroom",
    },
    classeviva: {
      trigger: "Carnet de notes",
      title: "Synchroniser ClasseViva",
      description: "Importez devoirs, contrôles et événements de l'agenda. Vos identifiants ne sont pas enregistrés : ils sont utilisés uniquement pour cette synchronisation.",
      uidLabel: "Code utilisateur / e-mail",
      uidPlaceholder: "Ex. S1234567L",
      passLabel: "Mot de passe",
      disclaimer: "Intégration non officielle — si ClasseViva change son API, l'import pourrait cesser de fonctionner temporairement.",
      syncing: "Synchronisation…",
      syncNow: "Synchroniser maintenant",
    },
    axios: {
      trigger: "Axios",
      title: "Synchroniser le carnet Axios",
      description: "J'importe devoirs, contrôles et sujets de cours depuis le registre électronique Axios. Les identifiants ne servent qu'à cette synchronisation.",
      schoolLabel: "Code de l'établissement",
      schoolPlaceholder: "Ex. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Identifiants incorrects : vérifie identifiant, mot de passe et code de l'établissement.",
      REGISTRO_TIMEOUT: "Le registre n'a pas répondu à temps. Réessaie dans une minute.",
      REGISTRO_UNREACHABLE: "Le registre est injoignable pour le moment. Réessaie plus tard.",
      REGISTRO_BAD_RESPONSE: "Réponse inattendue du registre : ton établissement n'expose peut-être pas cette API.",
      REGISTRO_MISSING_SCHOOL: "Saisis le code de l'établissement pour connecter Axios.",
    },
    fields: {
      title: "Titre",
      type: "Type",
      subject: "Matière",
      subjectOptional: "Matière (facultatif)",
      date: "Date",
      time: "Heure",
      allDay: "Toute la journée",
      notes: "Notes",
      notesOptional: "Notes (facultatif)",
    },
    placeholders: {
      title: "Ex. Contrôle de Mathématiques",
      subject: "Ex. Histoire",
    },
    actions: {
      delete: "Supprimer",
      close: "Fermer",
      save: "Enregistrer",
      saving: "Enregistrement…",
      cancel: "Annuler",
      add: "Ajouter",
    },
    toast: {
      updated: "Événement mis à jour",
      deleted: "Événement supprimé",
      added: "Événement ajouté",
      syncSuccess: "{{count}} événements synchronisés depuis ClasseViva",
    },
    errors: {
      mustBeLoggedIn: "Vous devez être connecté",
    },
  },
  de: {
    title: "Planer",
    subtitle: "Dein intelligenter Lernkalender.",
    today: "Heute",
    addEvent: "Ereignis hinzufügen",
    noEventsToday: "Für diesen Tag sind keine Ereignisse geplant.",
    allDayShort: "Tag",
    circleColor: "Kreisfarbe:",
    circlePreview: "Kreisvorschau:",
    eventTypes: {
      verifica: "Test",
      compito: "Hausaufgabe",
      orale: "Mündliche Prüfung",
      hobby: "Hobby",
      event: "Ereignis",
      task: "Aufgabe",
    },
    autoPlanning: {
      title: "Automatische Planung",
      description: "Die KI verteilt Tests, Wiederholungen und Pausen, sobald du eine Prüfung hinzufügst oder das Klassenbuch synchronisierst.",
    },
    edit: {
      title: "Ereignis bearbeiten",
      titleReadOnly: "Ereignisdetails",
      description: "Titel, Datum, Uhrzeit, Typ oder Notizen bearbeiten.",
      descriptionImported: "Aus {{source}} importiertes Ereignis: nur teilweise bearbeitbar.",
    },
    add: {
      title: "Neues Ereignis",
      description: "Die Kreisfarbe im Kalender richtet sich nach dem Ereignistyp.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Hausaufgaben aus Google Classroom",
      description: "Füge den geheimen iCal-Link des Kurskalenders ein: Ich importiere Fristen und Aufgaben ohne Passwort.",
      previewDesc: "Prüfe, was ich gefunden habe, und bestätige.",
      urlLabel: "iCal-Link des Kalenders",
      help1: "Öffne Google Classroom → Kurs → Aufgaben → Google Kalender.",
      help2: "Öffne in Google Kalender die Einstellungen des Kurskalenders.",
      help3: "Kopiere die geheime Adresse im iCal-Format und füge sie hier ein.",
      countItems: "Aufgaben / Fristen",
      countSubjects: "Erkannte Fächer",
      empty: "Keine kommenden Fristen in diesem Kalender gefunden.",
      optCalendar: "Zum Kalender hinzufügen",
      optHomework: "Zur Aufgabenliste hinzufügen",
      load: "Kalender lesen",
      confirm: "Importieren",
      imported: "{{events}} Termine und {{homework}} Aufgaben aus Classroom importiert",
    },
    classeviva: {
      trigger: "Klassenbuch",
      title: "ClasseViva synchronisieren",
      description: "Importiere Hausaufgaben, Tests und Termine. Deine Zugangsdaten werden nicht gespeichert: sie werden nur für diese Synchronisierung verwendet.",
      uidLabel: "Benutzercode / E-Mail",
      uidPlaceholder: "Z. B. S1234567L",
      passLabel: "Passwort",
      disclaimer: "Inoffizielle Integration — wenn ClasseViva seine API ändert, könnte der Import vorübergehend nicht funktionieren.",
      syncing: "Synchronisiere…",
      syncNow: "Jetzt synchronisieren",
    },
    axios: {
      trigger: "Axios",
      title: "Axios-Klassenbuch synchronisieren",
      description: "Ich importiere Hausaufgaben, Tests und Unterrichtsthemen aus dem Axios-Register. Die Zugangsdaten werden nur für diese Synchronisierung genutzt.",
      schoolLabel: "Schulcode",
      schoolPlaceholder: "Z. B. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Falsche Zugangsdaten: Benutzername, Passwort und Schulcode prüfen.",
      REGISTRO_TIMEOUT: "Das Register hat nicht rechtzeitig geantwortet. Versuche es gleich noch einmal.",
      REGISTRO_UNREACHABLE: "Das Register ist gerade nicht erreichbar. Versuche es später.",
      REGISTRO_BAD_RESPONSE: "Unerwartete Antwort des Registers: Deine Schule stellt diese API vielleicht nicht bereit.",
      REGISTRO_MISSING_SCHOOL: "Gib den Schulcode ein, um Axios zu verbinden.",
    },
    fields: {
      title: "Titel",
      type: "Typ",
      subject: "Fach",
      subjectOptional: "Fach (optional)",
      date: "Datum",
      time: "Uhrzeit",
      allDay: "Ganztägig",
      notes: "Notizen",
      notesOptional: "Notizen (optional)",
    },
    placeholders: {
      title: "Z. B. Mathe-Test",
      subject: "Z. B. Geschichte",
    },
    actions: {
      delete: "Löschen",
      close: "Schließen",
      save: "Speichern",
      saving: "Speichern…",
      cancel: "Abbrechen",
      add: "Hinzufügen",
    },
    toast: {
      updated: "Ereignis aktualisiert",
      deleted: "Ereignis gelöscht",
      added: "Ereignis hinzugefügt",
      syncSuccess: "{{count}} Ereignisse von ClasseViva synchronisiert",
    },
    errors: {
      mustBeLoggedIn: "Du musst angemeldet sein",
    },
  },
  pt: {
    title: "Planejador",
    subtitle: "O seu calendário de estudo inteligente.",
    today: "Hoje",
    addEvent: "Adicionar evento",
    noEventsToday: "Nenhum evento agendado para este dia.",
    allDayShort: "Dia",
    circleColor: "Cor do círculo:",
    circlePreview: "Pré-visualização do círculo:",
    eventTypes: {
      verifica: "Prova",
      compito: "Trabalho de casa",
      orale: "Prova oral",
      hobby: "Hobby",
      event: "Evento",
      task: "Atividade",
    },
    autoPlanning: {
      title: "Planejamento automático",
      description: "A IA distribuirá provas, revisões e pausas quando você adicionar um exame ou sincronizar o registro.",
    },
    edit: {
      title: "Editar evento",
      titleReadOnly: "Detalhes do evento",
      description: "Edite título, data, hora, tipo ou notas.",
      descriptionImported: "Evento importado de {{source}}: editável apenas em parte.",
    },
    add: {
      title: "Novo evento",
      description: "A cor do círculo no calendário segue o tipo de evento.",
    },
    classroom: {
      trigger: "Classroom",
      title: "Tarefas do Google Classroom",
      description: "Cole o link iCal secreto do calendário do curso: importo prazos e tarefas sem pedir a senha.",
      previewDesc: "Confira o que encontrei e confirme.",
      urlLabel: "Link iCal do calendário",
      help1: "Abra o Google Classroom → curso → Atividades → Google Agenda.",
      help2: "No Google Agenda abra as configurações do calendário do curso.",
      help3: "Copie o endereço secreto em formato iCal e cole aqui.",
      countItems: "Tarefas / prazos",
      countSubjects: "Matérias detectadas",
      empty: "Nenhum prazo futuro encontrado neste calendário.",
      optCalendar: "Adicionar ao calendário",
      optHomework: "Adicionar à lista de tarefas",
      load: "Ler o calendário",
      confirm: "Importar",
      imported: "Importados {{events}} eventos e {{homework}} tarefas do Classroom",
    },
    classeviva: {
      trigger: "Registro",
      title: "Sincronizar ClasseViva",
      description: "Importe trabalhos, provas e eventos da agenda. Suas credenciais não são salvas: são usadas apenas para esta sincronização.",
      uidLabel: "Código de usuário / e-mail",
      uidPlaceholder: "Ex. S1234567L",
      passLabel: "Senha",
      disclaimer: "Integração não oficial — se o ClasseViva mudar sua API, a importação pode parar de funcionar temporariamente.",
      syncing: "Sincronizando…",
      syncNow: "Sincronizar agora",
    },
    axios: {
      trigger: "Axios",
      title: "Sincronizar registo Axios",
      description: "Importo trabalhos, testes e temas das aulas do registo eletrónico Axios. As credenciais são usadas apenas nesta sincronização.",
      schoolLabel: "Código da escola",
      schoolPlaceholder: "Ex. 80012345678",
    },
    registroErrors: {
      REGISTRO_BAD_CREDENTIALS: "Credenciais erradas: verifica utilizador, palavra-passe e código da escola.",
      REGISTRO_TIMEOUT: "O registo não respondeu a tempo. Tenta de novo dentro de um minuto.",
      REGISTRO_UNREACHABLE: "O registo está indisponível neste momento. Tenta mais tarde.",
      REGISTRO_BAD_RESPONSE: "Resposta inesperada do registo: a tua escola pode não disponibilizar esta API.",
      REGISTRO_MISSING_SCHOOL: "Introduz o código da escola para ligar o Axios.",
    },
    fields: {
      title: "Título",
      type: "Tipo",
      subject: "Matéria",
      subjectOptional: "Matéria (opcional)",
      date: "Data",
      time: "Hora",
      allDay: "Dia inteiro",
      notes: "Notas",
      notesOptional: "Notas (opcional)",
    },
    placeholders: {
      title: "Ex. Prova de Matemática",
      subject: "Ex. História",
    },
    actions: {
      delete: "Excluir",
      close: "Fechar",
      save: "Salvar",
      saving: "Salvando…",
      cancel: "Cancelar",
      add: "Adicionar",
    },
    toast: {
      updated: "Evento atualizado",
      deleted: "Evento excluído",
      added: "Evento adicionado",
      syncSuccess: "{{count}} eventos sincronizados do ClasseViva",
    },
    errors: {
      mustBeLoggedIn: "Você precisa estar logado",
    },
  },
};
